import { createGlobalStyle } from "styled-components";

import librasFontUrl from "../assets/fonts/Libras2020-Regular.ttf";

const GlobalStyle = createGlobalStyle`
  @font-face {
    font-family: "Libras2020";
    src: url(${librasFontUrl}) format("truetype");
    font-weight: normal;
    font-style: normal;
  }

  :root {
    --color-background: ${({ theme }) => theme.colors.background};
    --color-surface: ${({ theme }) => theme.colors.surface};
    --color-primary-lighter: ${({ theme }) => theme.colors.primaryLighter};
    --color-primary-light: ${({ theme }) => theme.colors.primaryLight};
    --color-primary: ${({ theme }) => theme.colors.primary};
    --color-primary-dark: ${({ theme }) => theme.colors.primaryDark};
    --color-primary-darker: ${({ theme }) => theme.colors.primaryDarker};
    --color-secondary: ${({ theme }) => theme.colors.secondary};
    --color-secondary-light: ${({ theme }) => theme.colors.secondaryLight};
    --color-secondary-dark: ${({ theme }) => theme.colors.secondaryDark};
    --color-accent: ${({ theme }) => theme.colors.accent};
    --color-title-in-primary: ${({ theme }) => theme.colors.titleOnPrimary};
    --color-text-in-primary: ${({ theme }) => theme.colors.textOnPrimary};
    --color-text-title: ${({ theme }) => theme.colors.textTitle};
    --color-text-complement: ${({ theme }) => theme.colors.textComplement};
    --color-text-base: ${({ theme }) => theme.colors.textBase};
    --color-text-secondary: ${({ theme }) => theme.colors.textSecondary};
    --color-text-tertiary: ${({ theme }) => theme.colors.textTertiary};
    --color-surface-secondary: ${({ theme }) => theme.colors.surfaceSecondary};
    --color-surface-tertiary: ${({ theme }) => theme.colors.surfaceTertiary};
    --color-card: ${({ theme }) => theme.colors.card};
    --color-border: ${({ theme }) => theme.colors.border};
    --color-line-in-white: ${({ theme }) => theme.colors.lineInWhite};
    --color-input-background: ${({ theme }) => theme.colors.inputBackground};
    --color-button-text: ${({ theme }) => theme.colors.buttonText};
    --color-box-base: ${({ theme }) => theme.colors.boxBase};
    --color-box-footer: ${({ theme }) => theme.colors.boxFooter};
    --color-info: ${({ theme }) => theme.colors.info};
    --color-success: ${({ theme }) => theme.colors.success};
    --color-warning: ${({ theme }) => theme.colors.warning};
    --color-danger: ${({ theme }) => theme.colors.danger};
    --md3-shape-card: ${({ theme }) => theme.md3.shape.extraLarge}px;
    --md3-shape-button: ${({ theme }) => theme.md3.shape.full}px;
    --md3-shape-field: ${({ theme }) => theme.md3.shape.medium}px;
    --md3-motion-duration: ${({ theme }) => theme.md3.motion.duration.short4}ms;
    --md3-motion-easing: ${({ theme }) => theme.md3.motion.easing.expressive};
    --font-weight-body: ${({ theme }) => theme.fontWeight.body};
    --font-weight-medium: ${({ theme }) => theme.fontWeight.medium};
    --font-weight-semibold: ${({ theme }) => theme.fontWeight.semibold};
    --font-weight-bold: ${({ theme }) => theme.fontWeight.bold};
    --font-weight-extrabold: ${({ theme }) => theme.fontWeight.extrabold};
    --layout-content-max: ${({ theme }) => theme.layout.contentMaxPx}px;
    --layout-content-standard: ${({ theme }) =>
      theme.layout.contentStandardPx}px;
    --layout-header-footer-inner: ${({ theme }) =>
      theme.layout.headerFooterInnerMaxPx}px;
    --layout-app-shell-max: ${({ theme }) =>
      theme.layout.appShellNarrowMaxPx}px;
    --font-size-xs: 11px;
    --font-size-sm: 14px;
    --font-size-base: 19px;
    --font-size-xl: 24px;
    --font-size-2xl: 24px;
    --line-height-sm: 24px;
    --line-height-base: 32px;
    --line-height-xl: 41px;
    font-size: 16px;
  }

  @media (min-width: ${({ theme }) => theme.layout.mobileFirstExpandPx}px) {
    :root {
      --layout-app-shell-max: ${({ theme }) =>
        theme.layout.appShellWideMaxPx}px;
    }
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
    color-scheme: ${({ theme }) => (theme.isDarkMode ? "dark" : "light")};
  }

  html,
  body,
  #root {
    min-height: 100%;
  }

  body {
    background: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    overflow-x: hidden;
    font-weight: ${({ theme }) => theme.fontWeight.body};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    transition: background 0.25s ease, color 0.25s ease;
  }

  #root {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100vh;
    min-height: 100dvh;
  }

  #root > * {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    width: 100%;
  }

  .app-root {
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 100%;
    min-height: 100dvh;
  }

  .app-shell {
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    min-height: 100%;
    overflow-x: hidden;
    box-sizing: border-box;
  }

  .app-shell > * {
    flex: 1 0 auto;
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  body,
  input,
  textarea {
    font-family: ${({ theme }) => theme.font.family};
    font-size: 19px;
    line-height: 32px;
    font-weight: ${({ theme }) => theme.fontWeight.body};
  }

  button {
    font-family: ${({ theme }) => theme.font.familyHeading};
    font-size: 19px;
    line-height: 32px;
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: ${({ theme }) => theme.font.familyHeading};
    font-weight: ${({ theme }) => theme.fontWeight.bold};
  }

  p,
  span,
  li,
  label {
    font-weight: inherit;
  }

  strong,
  b {
    font-weight: ${({ theme }) => theme.fontWeight.bold};
  }

  /* VLibras — widget à esquerda */
  div[vw].enabled {
    right: auto !important;
    left: 0 !important;
  }

  [vw-access-button] {
    right: auto !important;
    left: max(8px, env(safe-area-inset-left)) !important;
    top: 50% !important;
    bottom: auto !important;
    transform: translateY(-50%) !important;
  }

  [vw-plugin-wrapper] {
    left: 0 !important;
    right: auto !important;
  }

  .container {
    width: 100%;
    max-width: var(--layout-content-standard);
    margin-left: auto;
    margin-right: auto;
    padding-left: max(1.2rem, env(safe-area-inset-left));
    padding-right: max(1.2rem, env(safe-area-inset-right));
  }

  @media (min-width: 700px) {
    .container {
      padding-left: max(1.6rem, env(safe-area-inset-left));
      padding-right: max(1.6rem, env(safe-area-inset-right));
    }
    .container Input {
      width: 80%;
    }
    .container .ant-row-ant-form-item {
      width: 80%;
    }
  }

  .container Input {
    width: 100%;
    height: 5.6rem;
    margin-top: 0.8rem;
    margin-bottom: 0.8rem;
    border-radius: 0.8rem;
    background-color: var(--color-input-background);
    border: 1px solid var(--color-line-in-white);
    outline: 0;
    padding: 0 1.6rem;
    font: 19px ${({ theme }) => theme.font.family};
  }

  .container Input:focus {
    border-bottom: 2px solid var(--color-primary-light);
    border-width: calc(100% - 0.2rem);
  }

  .container TextArea:focus {
    border-bottom: 2px solid var(--color-primary-light);
    border-width: calc(100% - 3.2rem);
  }

  .container label {
    font-size: 14px;
  }

  .container TextArea {
    width: 100%;
    height: 16rem;
    min-height: 8rem;
    margin-top: 0.8rem;
    border-radius: 0.8rem;
    background: var(--color-input-background);
    border: 1px solid var(--color-line-in-white);
    outline: 0;
    resize: vertical;
    padding: 1.2rem 1.6rem;
    font: 19px ${({ theme }) => theme.font.family};
  }

  .InputBlock {
    width: 100%;
    height: 5.6rem;
    border-radius: 0.8rem;
    outline: 0;
    font: 19px ${({ theme }) => theme.font.family};
  }

  .container .ant-select {
    width: 100%;
    height: 5.6rem;
    margin-top: 0.8rem;
    border-radius: 0.8rem;
    background: var(--color-input-background);
    border: 1px solid var(--color-line-in-white);
    outline: 0;
    font: 19px ${({ theme }) => theme.font.family};
  }

  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    height: 100%;
    position: relative;
    background-color: var(--color-input-background);
    border: 0;
    border-radius: 5px;
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  }

  .container
    .ant-select-single.ant-select-show-arrow
    .ant-select-selection-item:hover {
    border-bottom: 2px solid var(--color-primary-light);
    border-width: calc(100% - 3.2rem);
  }

  .ant-select-single.ant-select-show-arrow .ant-select-selection-item,
  .ant-select-single.ant-select-show-arrow .ant-select-selection-placeholder {
    display: flex;
    padding-right: 18px;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }

  .cardContent {
    display: flex;
    margin-bottom: 1.6rem;
    width: 300px;
    justify-content: center;
  }

  .card {
    background: var(--color-card);
    width: 100%;
    padding: 0 1.1rem;
    display: flex;
    justify-content: space-between;
  }

  .ant-card-bordered {
    border: 1px solid var(--color-border);
    width: 27rem;
  }

  .ant-input-affix-wrapper:focus {
    border: none;
    background: var(--color-input-background) !important;
  }

  .ant-form-item-control-input {
    position: relative;
    display: flex;
    align-items: center;
    padding: 0;
  }

  .ant-form-item-label > label {
    font-size: 14px !important;
    line-height: 24px !important;
    font-weight: var(--font-weight-semibold) !important;
    color: var(--color-text-title) !important;
  }

  .swal2-popup,
  .swal2-container {
    display: none !important;
  }

  .ant-table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    max-width: 100%;
  }

  .ant-table {
    font-size: 19px !important;
  }

  .ant-table-thead > tr > th {
    background: ${({ theme }) => theme.colors.tableHeaderBg} !important;
    color: ${({ theme }) => theme.colors.tableHeaderText} !important;
    font-weight: var(--font-weight-semibold) !important;
    font-size: 14px !important;
    border-bottom: 2px solid ${({ theme }) =>
      theme.colors.tableRowBorder} !important;
  }

  .ant-table-tbody > tr > td {
    font-size: 19px !important;
    font-weight: var(--font-weight-body) !important;
    border-bottom: 1px solid ${({ theme }) =>
      theme.colors.tableRowBorder} !important;
    color: ${({ theme }) => theme.colors.tableCellText} !important;
  }

  .ant-table-tbody > tr:hover > td {
    background: ${({ theme }) => theme.colors.tableRowHover} !important;
  }

  .ant-btn-primary {
    color: var(--color-button-text) !important;
    background: var(--color-primary) !important;
    border-color: var(--color-primary) !important;
    border-radius: var(--md3-shape-button) !important;
    min-height: 48px;
    height: auto;
    padding: 0 24px !important;
    font-family: ${({ theme }) => theme.font.familyHeading} !important;
    font-weight: var(--font-weight-semibold) !important;
    box-shadow: none !important;
    text-shadow: none !important;
    transition: transform var(--md3-motion-duration) var(--md3-motion-easing),
      background var(--md3-motion-duration) ease;
  }

  .ant-btn-primary:active:not(:disabled) {
    transform: scale(0.96);
  }

  .ant-btn-primary:hover:not(:disabled) {
    background: var(--color-primary-dark) !important;
    border-color: var(--color-primary-dark) !important;
  }

  .ant-btn {
    border-radius: var(--md3-shape-button) !important;
    font-family: ${({ theme }) => theme.font.familyHeading} !important;
    font-weight: var(--font-weight-semibold) !important;
    font-size: 19px !important;
    line-height: 32px !important;
    min-height: 40px;
    height: auto;
    box-shadow: none !important;
  }

  .ant-btn-default {
    border-color: var(--color-primary) !important;
    color: var(--color-primary) !important;
    background: transparent !important;
  }

  .ant-btn-default:hover:not(:disabled) {
    color: var(--color-primary-dark) !important;
    border-color: var(--color-primary-dark) !important;
    background: color-mix(
      in srgb,
      var(--color-primary) 8%,
      transparent
    ) !important;
  }

  .ant-btn-link,
  .ant-btn-text {
    color: var(--color-secondary) !important;
    font-weight: var(--font-weight-medium) !important;
    min-height: 44px;
    border-radius: 12px !important;
  }

  .ant-btn-dangerous,
  .ant-btn-dangerous.ant-btn-primary {
    background: var(--color-danger) !important;
    border-color: var(--color-danger) !important;
  }

  .ant-input,
  .ant-input-affix-wrapper {
    border-radius: var(--md3-shape-field) !important;
    border-color: ${({ theme }) => theme.colors.antBorder} !important;
    font-size: 19px !important;
    min-height: 56px;
  }

  .ant-input:focus,
  .ant-input-affix-wrapper-focused {
    border-color: var(--color-primary) !important;
    box-shadow: 0 0 0 2px ${({ theme }) =>
      theme.colors.antInputFocusRing} !important;
  }

  .ant-modal {
    max-width: min(calc(100vw - 24px), 520px);
    margin: 16px auto;
    padding-bottom: env(safe-area-inset-bottom);
    top: 50%;
    transform: translateY(-50%);
  }

  .ant-modal-content {
    border-radius: ${({ theme }) => theme.borderRadius.xl}px !important;
    overflow: hidden;
    background: ${({ theme }) => theme.colors.surface} !important;
    border: 1px solid ${({ theme }) => theme.colors.border};
    box-shadow: ${({ theme }) => theme.shadows.md3Level3} !important;
    padding: 0 !important;
  }

  .ant-modal-header {
    background: transparent !important;
    border-bottom: 1px solid var(--color-border) !important;
    padding: 20px 24px 16px !important;
    margin-bottom: 0 !important;
  }

  .ant-modal-title {
    color: var(--color-text-title) !important;
    font-size: 24px !important;
    font-weight: var(--font-weight-bold) !important;
    font-family: ${({ theme }) => theme.font.familyHeading} !important;
    line-height: 1.3 !important;
  }

  .ant-modal-body {
    padding: 8px 24px 16px !important;
    color: var(--color-text-secondary) !important;
    font-size: 14px !important;
    line-height: 24px !important;
  }

  .ant-modal-footer {
    border-top: none !important;
    padding: 0 24px 24px !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 8px !important;
  }

  .ant-modal-footer .ant-btn {
    width: 100% !important;
    margin: 0 !important;
    min-height: 56px !important;
  }

  .ant-modal-close {
    top: 12px !important;
    inset-inline-end: 12px !important;
    width: 44px !important;
    height: 44px !important;
    border-radius: 9999px !important;
  }

  .ant-modal-close-x {
    color: var(--color-text-secondary) !important;
    line-height: 44px !important;
  }

  .ant-modal-confirm .ant-modal-confirm-body {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .ant-modal-confirm .ant-modal-confirm-title {
    color: var(--color-text-title) !important;
    font-weight: var(--font-weight-bold) !important;
  }

  .ant-modal-confirm .ant-modal-confirm-content {
    color: var(--color-text-secondary) !important;
  }

  .ant-pagination-item-active {
    border-color: var(--color-primary) !important;
  }

  .ant-pagination-item-active a {
    color: var(--color-primary) !important;
  }
`;

export default GlobalStyle;
